import { useEffect, useState } from 'react'
import { useLiveTune } from '../hooks/useLiveTune'
import { useRailInset } from '../hooks/useRailInset'
import { isBakeableUrl } from '../lib/bakeLightingApi'
import { activeEnvAssetId } from '../lib/activeEnvironmentAsset'
import { subscribeBake, getBakeState, ensureBakeStatus } from '../lib/bakeStatusStore'
import { bakeSettingsStore } from '../lib/bakeSettingsStore'
import { rowBakeState, bakePhaseLabel } from '../lib/bakeKind'
import { lightingLoadStatus } from '../lib/lightingStatus'

/**
 * Environment LIGHTING status (ENG-561/591) — top-left: a green dot when the
 * artifacts are loaded ("ready"), a spinner + % while baking, amber when not
 * baked, red on failure. No bake button — baking is triggered from the Build
 * menu. Live mode + environment only.
 *
 * LIGHTING ONLY. It used to carry a second Navmesh row, which reported "ready"
 * off `__NAVMESH__.state === 'applied'` — true of a navmesh that had merely
 * parsed, and it read "ready" on a scene whose ground clicks were failing. That
 * status now lives where a user asks the question: the outliner's NAVMESH row
 * (dot + meta) and NavmeshStatusChip, both through navmeshStatus.js. Navmesh bake
 * PROGRESS moved with it.
 *
 * "ready" reads the runtime load state (window.__LIGHT_PROBE_GRID__ /
 * __REFLECTION_CAPTURES__), so it's truthful rather than a bake-record echo.
 */
const DOT = {
  ready: '#22c55e', needs: '#f59e0b', baking: '#8b6cff', error: '#f87171', unsupported: '#9ca3af',
  loading: '#5cd4ff', // runtime is streaming baked artifacts into the scene
}
const LABEL = {
  ready: 'ready', needs: 'not baked', error: 'failed', unsupported: '.ply only',
}

export default function LightingStatusChip() {
  const tune = useLiveTune()
  // Only with a rendered environment — no environment, no bake UI. (The old
  // `live` prop gate is gone: the viewport is always live, and the store's
  // splatUrl IS the rendered environment now, so it can't be stale.)
  const splatUrl = tune?.environment?.splatUrl || null
  // The id the server stated, carried on the store — not parsed out of the URL.
  // `tune` is this component's subscription, so the resolve that fills it in for
  // a deep link re-renders us.
  const assetId = splatUrl ? activeEnvAssetId() : null
  const bakeable = isBakeableUrl(splatUrl)

  // Clear the docked Outliner rail, which shares this edge (0 when it's hidden).
  const railInset = useRailInset()

  const [, force] = useState(0)
  useEffect(() => subscribeBake(() => force(n => n + 1)), [])
  useEffect(() => { if (assetId && bakeable) ensureBakeStatus(assetId) }, [assetId, bakeable])

  const st = assetId ? getBakeState(assetId) : null
  const status = st?.status ?? 'none'

  // Close a stale Bake panel when the active environment changes (or clears).
  useEffect(() => {
    const s = bakeSettingsStore.getState()
    if (s.open && s.assetId !== assetId) bakeSettingsStore.close()
  }, [assetId])

  // Tick to refresh the % while baking and to pick up the runtime load states
  // (window globals, not React state). Faster cadence while baking.
  useEffect(() => {
    // Fast, steady cadence so the bake % and the runtime "loading N/M" read
    // update smoothly (the chip is tiny — the re-render cost is negligible).
    const t = setInterval(() => force(n => n + 1), 800)
    return () => clearInterval(t)
  }, [status])

  // Reloading the baked artifacts on baking -> ready is owned by bakeStatusStore
  // now (it bumps the env's bakeReload generation, which the artifact volumes
  // carry in their fetch-effect deps). This chip used to force it by writing
  // splatVersion = Date.now(), but splatVersion also cache-keys the ~130 MB splat
  // download (SplatEnvironment deps [url, version]), so every bake re-downloaded
  // the whole environment — and the reload was coupled to THIS chip observing the
  // transition. bakeReload reloads only the small artifact files, per env.

  if (!splatUrl) return null

  const baking = bakeable && status === 'baking'
  const pct = Math.round((st?.progress ?? 0) * 100)
  // Which row this bake is producing ('lighting' | 'navmesh'). Older state with
  // no kind (or a combined back-compat bake) → spin both rows.
  const bakingKind = st?.kind ?? null
  // Runtime artifact-load state (post-bake fetch into the scene). This is what
  // makes "loading reflections N/M" evident instead of a silent black ball.
  const grid = typeof window !== 'undefined' ? window.__LIGHT_PROBE_GRID__ : null
  const refl = typeof window !== 'undefined' ? window.__REFLECTION_CAPTURES__ : null
  const ll = lightingLoadStatus(grid, refl)
  const reflFrac = (refl?.state === 'pending' && refl?.total > 0)
    ? (Number(refl.loaded) || 0) / refl.total
    : null
  const lightingReady = ll.ready

  // Only spin the row this bake actually targets (navmesh build ≠ lighting).
  const rowKind = (ready, row) =>
    rowBakeState({ bakeable, baking, bakingKind, status, ready, row })

  const Row = ({ label, ready, row, loading = false, loadingLabel = '', loadingFrac = null }) => {
    const baseKind = rowKind(ready, row)
    // Runtime load (post-bake fetch) shows as a distinct, evident "loading"
    // state — never as "not baked" (the silent-black-ball bug). Server-side
    // baking still wins (it precedes the load).
    const kind = loading && baseKind !== 'baking' ? 'loading' : baseKind
    const dot = DOT[kind]
    const spinning = kind === 'baking' || kind === 'loading'
    // Staged-progress label, e.g. "Downloading (1/2)" (ENG-592). Falls back to
    // a bare % until the first phase report lands.
    const phaseText = kind === 'baking'
      ? bakePhaseLabel({ phase: st?.phase, stage: st?.stage, stages: st?.stages })
      : ''
    const statusText = kind === 'baking'
      ? `${phaseText ? phaseText + ' ' : ''}${pct}%`
      : kind === 'loading'
        ? (loadingLabel || 'loading…')
        : LABEL[kind]
    // Progress bar: bake %, or the reflection load fraction when known.
    const barFrac = kind === 'baking' ? pct / 100 : (kind === 'loading' ? loadingFrac : null)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {spinning ? (
            <span style={{
              width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
              border: `1.5px solid ${dot}44`, borderTopColor: dot,
              animation: 'lightchip-spin 0.8s linear infinite',
            }} />
          ) : (
            <span style={{
              width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
              background: dot, boxShadow: `0 0 5px ${dot}aa`,
            }} />
          )}
          <span style={{ minWidth: 58 }}>{label}</span>
          <span style={{ color: kind === 'loading' ? dot : '#9ca3af', fontSize: 11 }}>
            {statusText}
          </span>
        </div>
        {barFrac != null && (
          <div style={{ height: 3, borderRadius: 2, background: '#2a2a2a', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${Math.round(Math.min(1, Math.max(0, barFrac)) * 100)}%`,
              background: dot, transition: 'width 0.3s ease',
            }} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      data-testid="lighting-status-chip"
      style={{
        // Sit BELOW the always-visible AppMenu "Menu ▾" button (top:8 left:8)
        // so they don't overlap (ENG-592), and to the RIGHT of the docked
        // Outliner rail when it's open (ENG-895) — same z-index, so without the
        // inset DOM order alone decided who was visible.
        position: 'absolute', top: 46, left: 10 + railInset, zIndex: 20,
        display: 'flex', flexDirection: 'column', gap: 5,
        padding: '7px 11px', borderRadius: 8,
        background: 'rgba(0,0,0,0.72)', border: '1px solid #2a2a2a',
        color: '#e5e7eb', font: '500 12px/1.2 var(--m-font)',
        boxShadow: '0 1px 6px rgba(0,0,0,0.4)', userSelect: 'none', minWidth: 150,
      }}
      title={status === 'error' ? (st?.error || 'Bake failed') : undefined}
    >
      <Row label="Lighting" ready={lightingReady} row="lighting"
        loading={ll.loading} loadingLabel={ll.label} loadingFrac={reflFrac} />
      <style>{`@keyframes lightchip-spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
